import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { Layout } from "./components/Layout";
import { usePageTitle } from "./hooks/usePageTitle";

// Pages
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SetupAccount from "./pages/SetupAccount";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import PhysicianSchedule from "./pages/schedules/PhysicianSchedule";
import EmployeeSchedule from "./pages/schedules/EmployeeSchedule";
import Schedules from "./pages/Schedules";
import Timesheets from "./pages/Timesheets";
import Supplies from "./pages/Supplies";
import Training from "./pages/Training";
import Payroll from "./pages/Payroll";
import PayrollCalculator from "./pages/PayrollCalculator";
import Onboarding from "./pages/Onboarding";
import AdminPanel from "./pages/admin/AdminPanel";
import UserManagement from "./pages/admin/UserManagement";
import PayPeriods from "./pages/admin/PayPeriods";
import AuditLogs from "./pages/admin/AuditLogs";
import YTDManagement from "./pages/admin/YTDManagement";
import CRARemittance from "./pages/admin/CRARemittance";
import OperationsDashboard from "./pages/admin/OperationsDashboard";
import OnboardingStatusDashboard from "./pages/admin/OnboardingStatusDashboard";
import TrainingComplianceDashboard from "./pages/admin/TrainingComplianceDashboard";
import FinanceDashboard from "./pages/admin/FinanceDashboard";
import OperationsAssistant from "./pages/admin/OperationsAssistant";
import SupplyCatalog from "./pages/admin/SupplyCatalog";

// Page Title Component
function PageTitleManager() {
  usePageTitle();
  return null;
}

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Role-based Route for Employees/Physicians
const RoleRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Schedules redirect component
const SchedulesRedirect = () => {
  return <Navigate to="/schedules" replace />;
};

// Admin redirect component
const AdminRedirect = () => {
  return <Navigate to="/admin/users" replace />;
};

function AppRoutes() {
  return (
    <>
      <PageTitleManager />
      <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        } 
      />
      <Route 
        path="/reset-password" 
        element={<ResetPassword />} 
      />
      <Route 
        path="/setup-account" 
        element={<SetupAccount />} 
      />

      {/* Protected Routes - All Users */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } 
      />

      {/* Schedules - All Users */}
      <Route 
        path="/schedules" 
        element={
          <ProtectedRoute>
            <Schedules />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/schedules/physician" 
        element={
          <ProtectedRoute>
            <PhysicianSchedule />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/schedules/employee" 
        element={
          <ProtectedRoute>
            <EmployeeSchedule />
          </ProtectedRoute>
        } 
      />

      {/* Timesheets - Admin & Employee */}
      <Route 
        path="/timesheets" 
        element={
          <RoleRoute allowedRoles={['admin', 'employee']}>
            <Timesheets />
          </RoleRoute>
        } 
      />

      {/* Supplies - All Users */}
      <Route 
        path="/supplies" 
        element={
          <ProtectedRoute>
            <Supplies />
          </ProtectedRoute>
        } 
      />

      {/* Training - All Users */}
      <Route 
        path="/training" 
        element={
          <ProtectedRoute>
            <Training />
          </ProtectedRoute>
        } 
      />

      {/* Payroll - Admin & Employee */}
      <Route 
        path="/payroll" 
        element={
          <RoleRoute allowedRoles={['admin', 'employee']}>
            <Payroll />
          </RoleRoute>
        } 
      />

      {/* Payroll Calculator - Admin Only */}
      <Route 
        path="/payroll/calculator" 
        element={
          <ProtectedRoute adminOnly>
            <PayrollCalculator />
          </ProtectedRoute>
        } 
      />

      {/* Onboarding - Admin & Employee */}
      <Route 
        path="/onboarding" 
        element={
          <RoleRoute allowedRoles={['admin', 'employee']}>
            <Onboarding />
          </RoleRoute>
        } 
      />

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminRedirect /></ProtectedRoute>} />
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute adminOnly>
            <UserManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/schedule-templates" 
        element={
          <ProtectedRoute adminOnly>
            <AdminPanel />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/pay-periods" 
        element={
          <ProtectedRoute adminOnly>
            <PayPeriods />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/documents" 
        element={
          <ProtectedRoute adminOnly>
            <AdminPanel />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/audit-logs" 
        element={
          <ProtectedRoute adminOnly>
            <AuditLogs />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/ytd-management" 
        element={
          <ProtectedRoute adminOnly>
            <YTDManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/cra-remittance" 
        element={
          <ProtectedRoute adminOnly>
            <CRARemittance />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/operations" 
        element={
          <ProtectedRoute adminOnly>
            <OperationsDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/onboarding-status" 
        element={
          <ProtectedRoute adminOnly>
            <OnboardingStatusDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/training-compliance" 
        element={
          <ProtectedRoute adminOnly>
            <TrainingComplianceDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/finance" 
        element={
          <ProtectedRoute adminOnly>
            <FinanceDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/assistant" 
        element={
          <ProtectedRoute adminOnly>
            <OperationsAssistant />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/supply-catalog" 
        element={
          <ProtectedRoute adminOnly>
            <SupplyCatalog />
          </ProtectedRoute>
        } 
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
